/**
 * Created by Abner Sui on 02/20/2019.
 * Description:
 *
 * ------ maintenance history ------
 * 05/29/2019   Yoyo Fang   Make context menu items configurable
 */

import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { AppState } from '../../redux';
import { Store, select } from '@ngrx/store';
import { RefinebyConfig, RefinebyStatus, RefinebyWidgetAction, RefinebyType, RefinebySubConfig } from '../../widgets/refineby/refineby-view.model';
import { take } from 'rxjs/operators';
import { ResearchRefinebyService } from '../../services/research-refineby.service';
import { ResearchRefinebyConfig, QueryInfo } from '../../pages/research/research.view-model';
import { Subscription } from 'rxjs';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { DeviceDetectorService } from 'ngx-device-detector';
import { adhocTitleSelector } from '../../redux/reducers/adhoc.reducer';
import { Adhoc } from '../../tamalelibs/models/adhoc.model';
import * as _lodash from 'lodash';
import { LONG_TOUCH_INTERVAL } from '../../tamalelibs/constants/business.constants';
import { ResearchScreenActionTypes } from '../../redux/actions/research-screen.actions';
import { businessConstants } from '../../constants/business.constants';
import { ShowAsMode } from '../display/display.model';
import { ElementRef } from '@angular/core';
import { ArrayHelperService } from '../../tamalelibs/services/array-helper.service';

@Component({
    selector: 'research-refineby',
    templateUrl: './research-refineby.component.html',
    styleUrls: ['./research-refineby.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResearchRefinebyComponent implements OnInit, OnDestroy {

    @Input()
    config: ResearchRefinebyConfig;

    refinebyConfig: RefinebyConfig;
    contextMenuConfig: ContextMenuConfig;
    isIPAD = false;
    reloading = false;
    updating = false;

    private _data: Map<string, Array<any>>;
    private _selectedValue: Map<string, Array<any>> = new Map();
    private _status: Map<string, RefinebyStatus> = new Map();
    private _category: Array<string>;
    private _categoryFilter: Object;
    private _queryInfo: QueryInfo = new QueryInfo();
    private _touchstart: number;
    private _destroySubscriptions: Array<Subscription> = [];
    private _showAsName = false;
    private _adhocTitle: Map<string, Adhoc>;
    private _adhocOrder: Array<Adhoc>;
    private _showAsMode: ShowAsMode;
    private _reset = false;

    constructor(private _store: Store<AppState>,
        private _refinebyService: ResearchRefinebyService,
        private _deviceService: DeviceDetectorService,
        private _changeDetectorRef: ChangeDetectorRef,
        private el: ElementRef,
    ) {
        this.refinebyConfig = new RefinebyConfig();
        this.isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
    }

    ngOnInit() {
        this._initContextMenu();
        this._destroySubscriptions.push(
            this._store.pipe(
                select(this.config.getResearchViewRefinebyDataStateSelector),
            ).subscribe(data => this._onSubscribeData(data))
        );

        this._destroySubscriptions.push(
            this.config.actions$.subscribe(action => this._onSubscribeActions(action))
        );

        this._store.pipe(
            select(this.config.getResearchViewRefinebyCategoryStateSelector),
            take(1),
        ).subscribe(data => {
            this._category = data;
        });

        this._store.pipe(
            select(this.config.getResearchViewRefinebySelectedValueStateSelector),
            take(1),
        ).subscribe(data => {
            if (data) {
                this._selectedValue = data;
            }
        });

        this._store.pipe(
            select(this.config.getResearchViewRefinebyStatusStateSelector),
            take(1),
        ).subscribe(data => {
            if (data) {
                this._status = data;
                this._resetStatus();
            }
        });

        this._store.pipe(
            select(this.config.getResearchViewRefinebyCategoryFilterStateSelector),
            take(1),
        ).subscribe(data => {
            if (data) {
                this._categoryFilter = data;
                Object.keys(this._categoryFilter).forEach(key => {
                    this.refinebyConfig.categoryFilter[key] = this._categoryFilter[key];
                });
            }
        });

        if (!this._category || this._category.length === 0) {
            this._category = this._refinebyService.DefaultCategory;
            this._store.dispatch({
                prefix: this.config.keyInStore,
                type: this.config.researchRefinebySetCategoryAction,
                payload: this._category,
            });
            this.refinebyConfig.category.push(...this._category);
        }

        this._store.pipe(
            select(adhocTitleSelector),
            take(1),
        ).subscribe(data => {
            this._adhocTitle = data || new Map();
            this._adhocOrder = Array.from(this._adhocTitle.values());
            this._changeDetectorRef.detectChanges();
            this._generateConfig();
            ArrayHelperService.sort(this._adhocOrder, 'id');
        });
        // Start generate config
        this._generateConfig();
        // End generate config
        this._destroySubscriptions.push(this.refinebyConfig.events$.subscribe(event => this._onSubscribeEvents(event)));
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    onContextMenu(event) {
        if (!event['refineby']) {
            this.contextMenuConfig.items = this.config.contextMenuItems;
            this._changeDetectorRef.detectChanges();
            this.contextMenuConfig.show$.next(new ContextMenuShowAction(event, ''));
            this.refinebyConfig.actions$.next({
                type: RefinebyWidgetAction.HIDE_ADD_LIST,
            });
        }
    }

    // For iPad
    touchstart() {
        this._touchstart = new Date().getTime();
    }

    // For iPad
    touchend(event) {
        if (this.isIPAD && new Date().getTime() - this._touchstart > LONG_TOUCH_INTERVAL) {
            event.clientX = event.pageX;
            event.clientY = event.pageY;
            this.onContextMenu(event);
        }
    }

    private _clearRefineByStore() {
        this._selectedValue.clear();
        this._categoryFilter = null;
        this._queryInfo.categoryFilter = this._categoryFilter;
        this._queryInfo.category = this._category;
        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.researchRefinebySetSelectedValueAction,
            payload: {
                selectedValue: this._selectedValue,
                categoryFilter: this._categoryFilter,
            }
        });
    }

    private _generateConfig() {
        if (this._data && this._data.size > 0 && this._category && this._category.length > 0) {
            this.refinebyConfig.displayConfig.splice(0, this.refinebyConfig.displayConfig.length); // Clear all items
            let needUpdateStatus = false;
            const standardLength = this._refinebyService.StandardCategory.length;
            this._category.forEach((id) => {
                const subConfig: RefinebySubConfig = this._refinebyService.generateSubConfig(id, this._data, this._selectedValue,
                    this._status, this._adhocTitle);
                if (subConfig != null) {
                    if (subConfig.type === RefinebyType.entity && subConfig.status.showAsName !== this._showAsName) {
                        subConfig.status.showAsName = this._showAsName;
                        this._status.set(subConfig.categoryId, subConfig.status);
                        needUpdateStatus = true;
                    }
                    const tempIndex = this._adhocOrder.findIndex(item => item.id === subConfig.categoryId);
                    if (tempIndex > -1) {
                        subConfig.defaultIndex = tempIndex + standardLength;
                    }
                    this.refinebyConfig.displayConfig.push(subConfig);
                }
            });
            if (needUpdateStatus) {
                this._resetStatus();
            }
            const emptyMap = new Map();
            const addConfig: Array<RefinebySubConfig> = [];
            this._adhocTitle.forEach((value, key, map) => {
                if (!this._category.includes(key)) {
                    const subConfig: RefinebySubConfig = this._refinebyService.generateSubConfig(key, emptyMap, emptyMap,
                        emptyMap, this._adhocTitle);
                    if (subConfig != null) {
                        addConfig.push(subConfig);
                    }
                }
            });
            this._refinebyService.StandardCategory.forEach(id => {
                if (!this._category.includes(id)) {
                    const subConfig: RefinebySubConfig = this._refinebyService.generateSubConfig(id, emptyMap, emptyMap,
                        emptyMap, emptyMap);
                    if (subConfig != null) {
                        addConfig.push(subConfig);
                    }
                }
            });
            addConfig.forEach((config, index) => {
                const tempIndex = this._adhocOrder.findIndex(item => item.id === config.categoryId);
                if (tempIndex > -1) {
                    config.defaultIndex = tempIndex + standardLength;
                }
            });
            ArrayHelperService.sort(addConfig, 'defaultIndex', true, true);
            this.refinebyConfig.addConfig.splice(0, this.refinebyConfig.addConfig.length);
            this.refinebyConfig.addConfig.push(...addConfig);

            const tempArray: Array<string> = _lodash.cloneDeep(this._category);
            this.refinebyConfig.category.splice(0, this.refinebyConfig.category.length);
            this.refinebyConfig.category.push(...tempArray);
            this._category = tempArray;
            let tempObj: Object = {};
            if (this._categoryFilter != null) {
                tempObj = _lodash.cloneDeep(this._categoryFilter);
            }
            // tslint:disable-next-line: forin
            for (const key in this.refinebyConfig.categoryFilter) {
                delete this.refinebyConfig.categoryFilter[key];
            }
            if (tempObj != null) {
                Object.keys(tempObj).forEach(key => {
                    this.refinebyConfig.categoryFilter[key] = tempObj[key];
                });
                this._categoryFilter = tempObj;
            }
            const tempMap: Map<string, Array<any>> = _lodash.cloneDeep(this._selectedValue);
            this.refinebyConfig.selectedValue.clear();
            tempMap.forEach((value: Array<any>, key: string) => {
                this.refinebyConfig.selectedValue.set(key, value);
            });
            this._selectedValue = tempMap;
            this.reloading = false;

            if (this._reset) {
                const content = this.el.nativeElement.querySelector('.content');
                setTimeout(() => {
                    content.scrollTop = content.scrollHeight - content.clientHeight;
                    this._reset = false;
                }, 0);
            }
        }
    }

    private _handleUpdate() {
        this.config.events$.next({
            type: 'UPDATE',
            payload: this._queryInfo
        });
    }

    private _initContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.forceRefresh = true;
        this.contextMenuConfig.isPassive = false;
        this.contextMenuConfig.items = this.config.contextMenuItems;
        this._destroySubscriptions.push(this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event)));
    }

    private _load(payload) {
        this.reloading = true;
        this._queryInfo = payload.queryInfo;
        this._queryInfo.category = this._category;
        this._showAsMode = payload.showAsMode;
        if (this._categoryFilter != null && Object.keys(this._categoryFilter).length === 0) {
            this._categoryFilter = null;
        }
        this._queryInfo.categoryFilter = this._categoryFilter;
        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.researchRefinebyLoadAction,
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });
    }

    private _onMenuSelect(event) {
        const type = +event.payload.id;
        let actionType: string;
        let data: any;
        switch (type) {
            case 0:
                actionType = 'RELATIONSHIP_TREE_STATUS';
                data = this.contextMenuConfig.items[type].checked;
                break;
            case 1:
                actionType = 'FINDER_STATUS';
                data = this.contextMenuConfig.items[type].checked;
                break;
            case 2:
                actionType = 'SHOW_AS_NAME';
                this.contextMenuConfig.items[3].checked = !this.contextMenuConfig.items[type].checked;
                this._changeShowAsName(this.contextMenuConfig.items[3].checked);
                data = this.contextMenuConfig.items[3].checked;
                break;
            case 3:
                actionType = 'SHOW_AS_NAME';
                this.contextMenuConfig.items[2].checked = !this.contextMenuConfig.items[type].checked;
                this._changeShowAsName(this.contextMenuConfig.items[type].checked);
                data = this.contextMenuConfig.items[3].checked;
                break;
            default:
                break;
        }
        this.config.events$.next({
            type: actionType,
            payload: data,
        });
    }

    private _onSubscribeActions(action: any) {
        if (action.type === 'UPDATE') {
            this._update(action.payload);
        } else if (action.type === 'RELOAD') {
            // Only reset showMore
            this._status.forEach((value, key, map) => {
                value.showMore = false;
            });
            this._resetStatus();
            this._load(action.payload);
        } else if (action.type === 'RESET_STATUS') {
            this._changeShowAsName(this.config.showAsName);
        } else if (action.type === 'RELOAD_DATA') {
            this._load(action.payload);
        }
    }

    private _onSubscribeData(data) {
        this._data = data;
        if (this.updating) {
            this._updateConfig();
        } else {
            this._generateConfig();
        }
        this._changeDetectorRef.detectChanges();
    }

    private _onSubscribeEvents(event) {
        const data = event.payload;
        if (event.type === RefinebyWidgetAction.SELECTED_OPTION_CHANGE) {
            this._selectedValue = data.selectedValue;
            this._categoryFilter = data.categoryFilter;
            if (this._categoryFilter != null && Object.keys(this._categoryFilter).length === 0) {
                this._categoryFilter = null;
            }
            this._queryInfo.categoryFilter = this._categoryFilter;
            this._queryInfo.category = this._category;
            this._store.dispatch({
                prefix: this.config.keyInStore,
                type: this.config.researchRefinebySetSelectedValueAction,
                payload: {
                    selectedValue: this._selectedValue,
                    categoryFilter: this._categoryFilter,
                }
            });
            this._handleUpdate();
        } else if (event.type === RefinebyWidgetAction.CLEAR_ALL) {
            this._clearRefineByStore();
            this.config.events$.next({
                type: 'RELOAD',
                payload: this._queryInfo
            });
        } else if (event.type === RefinebyWidgetAction.STATUS_CHANGE) {
            this._status = data.status;
            this._resetStatus();
        } else if (event.type === RefinebyWidgetAction.CHANGE_CATEGORY_ORDER) {
            this._category = data.category;
            this._store.dispatch({
                prefix: this.config.keyInStore,
                type: this.config.researchRefinebySetCategoryOrderAction,
                payload: this._category
            });
            this._changeResearchScreenStatus();
        } else if (event.type === RefinebyWidgetAction.RESET_CATEGORY) {
            this._reset = true;
            this._category = data.category;
            if (this._categoryFilter != null && Object.keys(this._categoryFilter).length === 0) {
                this._categoryFilter = null;
            }
            this._queryInfo.categoryFilter = this._categoryFilter;
            this._queryInfo.category = this._category;
            this._store.dispatch({
                prefix: this.config.keyInStore,
                type: this.config.researchRefinebySetCategoryAction,
                payload: this._category
            });
            this.config.events$.next({
                type: 'LOAD_REFINEBY',
                payload: this._queryInfo
            });
            this._changeResearchScreenStatus();
        }
    }

    private _changeResearchScreenStatus() {
        if (this.config.keyInStore !== businessConstants.researchScreen.defaultResearchScreenKey) {
            this._store.dispatch({
                type: ResearchScreenActionTypes.SET_HOME_SCREEN_CHANGE_STATUS,
                payload: {
                    screenKey: this.config.keyInStore,
                    component: ''
                }
            });
        } else {
            this._store.dispatch({
                type: ResearchScreenActionTypes.SET_SAVE_SCREEN_CHANGE_STATUS,
                payload: true
            });
        }
    }

    private _changeShowAsName(showAsName: boolean) {
        this._showAsName = showAsName;
        this.refinebyConfig.actions$.next({
            type: RefinebyWidgetAction.SHOW_AS_NAME,
            payload: this._showAsName,
        });
    }

    private _resetStatus(): void {
        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.researchRefinebySetCategoryStatusAction,
            payload: this._status,
        });
        this.refinebyConfig.actions$.next({
            type: RefinebyWidgetAction.SET_STATUS,
            payload: this._status,
        });
    }

    private _update(payload) {
        this.updating = true;
        this._queryInfo = payload.queryInfo;
        this._queryInfo.category = this._category;
        this._queryInfo.categoryFilter = this._categoryFilter;
        this._showAsMode = payload.showAsMode;
        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.researchRefinebyLoadAction,
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });
    }

    private _updateConfig() {
        if (this._data && this._data.size > 0 && this._category && this._category.length > 0) {
            this.refinebyConfig.displayConfig.forEach(config => {
                this._refinebyService.updateSubConfig(config, this._data, this._selectedValue, this._status, this._showAsName);
            });
        }
        this.updating = false;
        this.reloading = false;
    }

}
